'use client';

import React, { useState, useEffect } from 'react';
import { X, Mail, ArrowRight } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

export default function InterstitialModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const seen = sessionStorage.getItem('interstitial-dismissed');
    if (!seen) {
      const t = setTimeout(() => setOpen(true), 5000);
      return () => clearTimeout(t);
    }
  }, []);

  function dismiss() {
    sessionStorage.setItem('interstitial-dismissed', '1');
    setOpen(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    // Backend integration point: POST /api/newsletter with { email }
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setTimeout(dismiss, 2000);
    }, 900);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-modal w-full max-w-md relative overflow-hidden">
        {/* Red accent bar */}
        <div className="h-1.5 bg-primary w-full" />

        <div className="p-6 lg:p-8">
          {/* Close */}
          <button
            onClick={dismiss}
            aria-label="Close"
            className="absolute top-4 right-4 p-1.5 rounded-md hover:bg-muted transition-colors"
          >
            <X size={18} />
          </button>

          {/* Logo + heading */}
          <div className="flex items-center gap-2 mb-4">
            <AppLogo size={28} />
            <span className="font-extrabold text-foreground text-base tracking-tight">
              USA PEPTIDE PROS
            </span>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <Mail size={16} className="text-primary" />
            <span className="section-eyebrow">Industry Briefing</span>
          </div>

          <h2 className="text-xl font-800 text-foreground leading-tight">
            Stay ahead of regulatory changes and supply disruptions.
          </h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Join 200K+ peptide industry professionals who rely on our weekly intelligence digest. Regulatory alerts, COA standards updates, and market data — free.
          </p>

          {submitted ? (
            <div className="mt-5 flex items-center gap-2 text-sm text-green-600 font-600">
              <span>✓</span>
              <span>You&apos;re in. First issue arrives Monday.</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3">
              <input
                type="email"
                required
                placeholder="your@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-base"
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center py-3"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Subscribing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Get the Industry Briefing <ArrowRight size={16} />
                  </span>
                )}
              </button>
            </form>
          )}

          <p className="mt-3 text-xs text-muted-foreground text-center">
            No spam. Unsubscribe at any time.
          </p>
        </div>
      </div>
    </div>
  );
}